import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type {
  LegScoringStrategy,
  UnderModFavoriteFilter,
} from '@parlay-picker/shared/analysisConfig';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section } from './Section';
import { Toggle } from './Toggle';

interface ParlayIntelligenceSectionProps {
  cfg: AnalysisConfig;
  isExpanded: boolean;
  onToggleExpand: (sectionKey: string) => void;
  setConfig: React.Dispatch<React.SetStateAction<AnalysisConfig>>;
}

const STRATEGY_OPTIONS: Array<{
  value: LegScoringStrategy;
  label: string;
  stat: string;
  description: string;
  formula: string;
}> = [
  {
    value: 'ml_light_trend',
    label: 'Trend + ML',
    stat: 'Q1: 68%',
    description: 'Best — ML at 30% weight + hot/cold trend + contextual boosts',
    formula: 'Score = ML×0.3 + Trend×10 + Boosts×5',
  },
  {
    value: 'trend_boosted',
    label: 'Trend + Boosts',
    stat: 'Q1: 64%',
    description: 'No ML — uses trend, edge label, and context only',
    formula: 'Score = Trend×10 + Edge×3 + Boosts×5',
  },
  {
    value: 'opp_boosted_ml',
    label: 'Team-Boosted ML',
    stat: 'Legacy',
    description: 'ML score adjusted by opponent team hit rate',
    formula: 'Score = ML + (OppRate − 50)×0.5',
  },
  {
    value: 'ml_score',
    label: 'ML Score Only',
    stat: 'Baseline',
    description: 'Pure XGBoost model score, no boosts',
    formula: 'Score = ML score only',
  },
];

function ConfigSlider({
  label,
  description,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <div className={`${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-theme-text-secondary">{label}</span>
        <div className="flex items-center gap-2">
          <select
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            disabled={disabled}
            className="bg-theme-surface-hover text-gray-200 text-sm rounded px-2 py-1 border border-theme-border focus:border-purple-500 focus:outline-none disabled:cursor-not-allowed"
          >
            {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>
      {description && <p className="text-xs text-theme-text-tertiary mt-0.5">{description}</p>}
    </div>
  );
}

export function ParlayIntelligenceSection({
  cfg,
  isExpanded,
  onToggleExpand,
  setConfig,
}: ParlayIntelligenceSectionProps): ReactElement | null {
  const { isHidden } = useFeatureAccess();

  if (isHidden('ml_parlay_optimizer')) return null;
  const pi = cfg.parlayIntelligence ?? {};
  const mode = cfg.ml?.generationMode ?? 'shadow';
  const constructorActive = mode === 'optimized' || mode === 'ml';

  const updatePI = (key: string, value: string | number | boolean) => {
    setConfig((prev) => ({
      ...prev,
      parlayIntelligence: { ...prev.parlayIntelligence, [key]: value },
    }));
  };

  return (
    <Section
      title="Parlay Intelligence"
      sectionKey="parlayIntelligence"
      isExpanded={isExpanded}
      onToggleExpand={onToggleExpand}
      enabledCount={0}
      totalCount={0}
      showCount={false}
    >
      {!constructorActive ? (
        <p className="text-xs text-theme-text-tertiary p-2 bg-theme-surface rounded-lg">
          Select &quot;Optimized&quot; or &quot;Full ML&quot; generation mode to configure these
          settings.
        </p>
      ) : (
        <div className="space-y-3">
          {/* Strategy Filters — data-validated against 101 real bets */}
          <div>
            <span className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide">
              Strategy Filters
            </span>

            <div className="space-y-2 mt-2">
              <Toggle
                label="Reject Under + Mod Favorite"
                description="Under on teams favored by 1-6.5 pts hit 17%"
                checked={pi.rejectUnderModFavorite?.enabled !== false}
                onChange={(v) =>
                  setConfig((prev) => ({
                    ...prev,
                    parlayIntelligence: {
                      ...prev.parlayIntelligence,
                      rejectUnderModFavorite: {
                        ...(prev.parlayIntelligence?.rejectUnderModFavorite ?? {
                          spreadMin: -6.5,
                          spreadMax: -1,
                        }),
                        enabled: v,
                      } as UnderModFavoriteFilter,
                    },
                  }))
                }
              />
              <Toggle
                label="Reject Under + Away"
                description="Under on away players hit 23%"
                checked={pi.rejectUnderAway !== false}
                onChange={(v) => updatePI('rejectUnderAway', v)}
              />
              <Toggle
                label="Reject B2B + Over"
                description="Over on back-to-back players hit 0%"
                checked={pi.rejectB2bOver !== false}
                onChange={(v) => updatePI('rejectB2bOver', v)}
              />
              <Toggle
                label="Reject Cold + Weak Edge"
                description="Cold trend without strong edge hit 14%"
                checked={pi.rejectColdWeakEdge !== false}
                onChange={(v) => updatePI('rejectColdWeakEdge', v)}
              />
            </div>
          </div>

          {/* Score Boosts */}
          <div className="border-t border-theme-border pt-3 mt-1">
            <span className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide">
              Score Boosts
            </span>

            <div className="space-y-2 mt-2">
              <ConfigSlider
                label="Over + Mod Favorite"
                description="Points added when Over + team favored by 1-6.5 pts. Data: 64% hit rate."
                value={pi.overModFavoriteBoost ?? 3}
                min={0}
                max={6}
                onChange={(v) => updatePI('overModFavoriteBoost', v)}
                disabled={false}
              />
              <ConfigSlider
                label="Over Assists"
                description="Points added for Over assist props. Data: 62% hit rate."
                value={pi.overAssistsBoost ?? 2}
                min={0}
                max={5}
                onChange={(v) => updatePI('overAssistsBoost', v)}
                disabled={false}
              />
              <ConfigSlider
                label="Over Rebounds"
                description="Points added for Over rebound props. Data: 60% hit rate."
                value={pi.overReboundsBoost ?? 1}
                min={0}
                max={5}
                onChange={(v) => updatePI('overReboundsBoost', v)}
                disabled={false}
              />
              <ConfigSlider
                label="Hot Trend"
                description="Points added when player has Hot trend. Data: 56% hit rate vs 43% non-hot."
                value={pi.hotTrendBoost ?? 2}
                min={0}
                max={5}
                onChange={(v) => updatePI('hotTrendBoost', v)}
                disabled={false}
              />
            </div>
          </div>

          {/* Leg Scoring Strategy */}
          <div className="border-t border-theme-border pt-3 mt-1 space-y-1.5">
            <span className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide">
              Leg Scoring Strategy
            </span>
            <div className="space-y-1">
              {STRATEGY_OPTIONS.map((opt) => {
                const isActive = (pi.legScoringStrategy ?? 'ml_light_trend') === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => updatePI('legScoringStrategy', opt.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                      isActive
                        ? 'bg-purple-600/20 border border-purple-500/40 text-purple-300'
                        : 'bg-theme-surface hover:bg-theme-surface-hover text-theme-text-secondary border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{opt.label}</span>
                      <span
                        className={`text-xs ${isActive ? 'text-green-400' : 'text-theme-text-tertiary'}`}
                      >
                        {opt.stat}
                      </span>
                    </div>
                    <div className="text-xs text-theme-text-tertiary mt-0.5">{opt.description}</div>
                    <div className="text-xs text-theme-text-tertiary/60 mt-0.5 font-mono">
                      {opt.formula}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Constructor params */}
          <div className="border-t border-theme-border pt-3 mt-1">
            <span className="text-xs font-medium text-theme-text-tertiary uppercase tracking-wide">
              Constructor
            </span>

            <div className="space-y-2 mt-2">
              <ConfigSlider
                label="Max Legs to Consider"
                description="Top-scoring legs selected from the full generation pool for parlay construction."
                value={pi.maxLegsToConsider ?? 6}
                min={4}
                max={8}
                onChange={(v) => updatePI('maxLegsToConsider', v)}
                disabled={false}
              />
              <ConfigSlider
                label="Max Anchor Legs"
                description="High-confidence legs reused across multiple parlays as the backbone of your round-robin."
                value={pi.maxAnchorLegs ?? 2}
                min={1}
                max={3}
                onChange={(v) => updatePI('maxAnchorLegs', v)}
                disabled={false}
              />
              <ConfigSlider
                label="2-Leg Parlays"
                description="Max 2-leg parlays to build. Default: half of legs considered."
                value={pi.maxTwoLegParlays ?? Math.floor((pi.maxLegsToConsider ?? 6) / 2)}
                min={1}
                max={6}
                onChange={(v) => updatePI('maxTwoLegParlays', v)}
                disabled={false}
              />
              <ConfigSlider
                label="Max Leg Reuse"
                description="Max times any single leg can appear across all parlays. Higher = more overlap."
                value={pi.maxLegReuse ?? 2}
                min={1}
                max={5}
                onChange={(v) => updatePI('maxLegReuse', v)}
                disabled={false}
              />
              <ConfigSlider
                label="3-Leg Parlays"
                description="Number of 3-leg parlays to build. No 2-leg subset can duplicate an existing parlay."
                value={pi.threeLegParlays ?? 2}
                min={0}
                max={4}
                onChange={(v) => updatePI('threeLegParlays', v)}
                disabled={false}
              />

              <Toggle
                label="Include Lottery Parlay"
                description="Add a high-risk 4-6 leg parlay for upside"
                checked={pi.includeLotteryParlay ?? false}
                onChange={(v) => updatePI('includeLotteryParlay', v)}
              />
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
